import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Loader2, Save, Trash2, Plus, X, Eye, EyeOff } from "lucide-react";

const empty = {
  id: null,
  title: "",
  slug: "",
  category: "General",
  excerpt: "",
  content: "",
  cover_image: "",
  author: "KK Associates",
  tags: [],
  is_published: true,
};

export default function BlogManager() {
  const [blogs, setBlogs] = useState([]);
  const [editing, setEditing] = useState(null); // object or null
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState("");
  const [tagInput, setTagInput] = useState("");

  const load = async () => {
    const { data } = await api.get("/admin/blogs");
    setBlogs(data);
  };

  useEffect(() => { load(); }, []);

  const start = (b) => {
    setErr("");
    setEditing(b ? { ...b, tags: b.tags || [] } : { ...empty });
    setTagInput("");
  };

  const cancel = () => { setEditing(null); setErr(""); setTagInput(""); };

  const upd = (k) => (e) => setEditing({ ...editing, [k]: e.target.value });

  const addTag = () => {
    const t = tagInput.trim();
    if (!t) return;
    if (!editing.tags.includes(t)) setEditing({ ...editing, tags: [...editing.tags, t] });
    setTagInput("");
  };

  const removeTag = (t) => setEditing({ ...editing, tags: editing.tags.filter((x) => x !== t) });

  const save = async () => {
    setErr("");
    if (!editing.title.trim() || !editing.content.trim()) {
      setErr("Title and content are required.");
      return;
    }
    setSaving(true);
    try {
      const payload = {
        title: editing.title,
        slug: editing.slug || "",
        category: editing.category || "General",
        excerpt: editing.excerpt || "",
        content: editing.content,
        cover_image: editing.cover_image || "",
        author: editing.author || "KK Associates",
        tags: editing.tags || [],
        is_published: !!editing.is_published,
      };
      if (editing.id) await api.put(`/admin/blogs/${editing.id}`, payload);
      else await api.post("/admin/blogs", payload);
      setEditing(null);
      await load();
    } catch (e) {
      const d = e.response?.data?.detail;
      setErr(typeof d === "string" ? d : "Failed to save. Try again.");
    } finally {
      setSaving(false);
    }
  };

  const remove = async (b) => {
    if (!window.confirm(`Delete "${b.title}"?`)) return;
    await api.delete(`/admin/blogs/${b.id}`);
    await load();
  };

  const input = "w-full px-4 py-2.5 rounded-lg border border-slate-200 bg-white text-sm text-slate-900 placeholder:text-slate-400 focus:border-blue-500 focus:outline-none";

  if (editing) {
    return (
      <div>
        <div className="flex items-center justify-between mb-6">
          <h2 className="h-display text-3xl text-slate-900">{editing.id ? "Edit Blog Post" : "New Blog Post"}</h2>
          <button data-testid="blog-cancel" onClick={cancel} className="text-sm text-slate-600 hover:text-slate-900">← Back</button>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          <div className="lg:col-span-2 space-y-4">
            <div>
              <label className="text-xs uppercase tracking-widest text-slate-500">Title *</label>
              <input data-testid="blog-title" className={`${input} mt-1`} value={editing.title} onChange={upd("title")} placeholder="e.g. New GST rules for exporters" />
            </div>
            <div>
              <label className="text-xs uppercase tracking-widest text-slate-500">Excerpt</label>
              <textarea data-testid="blog-excerpt" rows={3} className={`${input} mt-1`} value={editing.excerpt} onChange={upd("excerpt")} placeholder="1-2 line summary shown on the blog list card." />
            </div>
            <div>
              <label className="text-xs uppercase tracking-widest text-slate-500">Content * <span className="text-slate-400 normal-case">(supports **bold** and paragraphs)</span></label>
              <textarea data-testid="blog-content" rows={16} className={`${input} mt-1 font-mono text-[13px]`} value={editing.content} onChange={upd("content")} placeholder="Write the full article here. Use blank lines between paragraphs and **text** for bold." />
            </div>
          </div>

          <aside className="space-y-4">
            <div className="rounded-2xl border border-slate-200 bg-white p-5 space-y-4">
              <div>
                <label className="text-xs uppercase tracking-widest text-slate-500">Category</label>
                <input data-testid="blog-category" className={`${input} mt-1`} value={editing.category} onChange={upd("category")} placeholder="e.g. GST, Income Tax, MSME" />
              </div>
              <div>
                <label className="text-xs uppercase tracking-widest text-slate-500">Slug <span className="text-slate-400 normal-case">(auto if blank)</span></label>
                <input data-testid="blog-slug" className={`${input} mt-1`} value={editing.slug} onChange={upd("slug")} placeholder="url-friendly-slug" />
              </div>
              <div>
                <label className="text-xs uppercase tracking-widest text-slate-500">Cover image URL</label>
                <input data-testid="blog-cover" className={`${input} mt-1`} value={editing.cover_image} onChange={upd("cover_image")} placeholder="https://…" />
                {editing.cover_image && (
                  <img src={editing.cover_image} alt="preview" className="mt-2 rounded-lg w-full aspect-[16/9] object-cover border border-slate-200" />
                )}
              </div>
              <div>
                <label className="text-xs uppercase tracking-widest text-slate-500">Author</label>
                <input data-testid="blog-author" className={`${input} mt-1`} value={editing.author} onChange={upd("author")} />
              </div>
              <div>
                <label className="text-xs uppercase tracking-widest text-slate-500">Tags</label>
                <div className="flex gap-2 mt-1">
                  <input data-testid="blog-tag-input" className={input} value={tagInput} onChange={(e) => setTagInput(e.target.value)} onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); addTag(); } }} placeholder="Type & press Enter" />
                  <button type="button" onClick={addTag} className="px-3 py-2 rounded-lg bg-slate-900 text-white text-sm">Add</button>
                </div>
                {editing.tags.length > 0 && (
                  <div className="mt-2 flex flex-wrap gap-1.5">
                    {editing.tags.map((t) => (
                      <span key={t} className="inline-flex items-center gap-1 text-xs bg-blue-50 text-blue-700 rounded-full px-2 py-1">
                        {t}<button onClick={() => removeTag(t)} className="hover:text-red-600"><X size={12} /></button>
                      </span>
                    ))}
                  </div>
                )}
              </div>
              <label className="flex items-center gap-2 text-sm text-slate-700 cursor-pointer">
                <input type="checkbox" data-testid="blog-published" checked={!!editing.is_published} onChange={(e) => setEditing({ ...editing, is_published: e.target.checked })} />
                Published (visible on public blog)
              </label>
            </div>

            {err && <div className="text-sm text-red-600" data-testid="blog-error">{err}</div>}

            <div className="flex gap-2">
              <button data-testid="blog-save" onClick={save} disabled={saving} className="btn-hover flex-1 inline-flex items-center justify-center gap-2 rounded-full bg-blue-700 text-white px-5 py-2.5 text-sm font-medium hover:bg-blue-800 disabled:opacity-60">
                {saving ? <><Loader2 size={14} className="animate-spin" /> Saving…</> : <><Save size={14} /> {editing.id ? "Update" : "Publish"}</>}
              </button>
              <button onClick={cancel} className="rounded-full border border-slate-300 px-5 py-2.5 text-sm text-slate-700 hover:bg-slate-100">Cancel</button>
            </div>
          </aside>
        </div>
      </div>
    );
  }

  return (
    <div>
      <div className="flex items-center justify-between">
        <h2 className="h-display text-3xl text-slate-900">Blog Posts ({blogs.length})</h2>
        <button data-testid="blog-new" onClick={() => start(null)} className="btn-hover inline-flex items-center gap-2 rounded-full bg-blue-700 text-white px-5 py-2.5 text-sm font-medium hover:bg-blue-800">
          <Plus size={14} /> New Post
        </button>
      </div>

      <div className="mt-6 rounded-2xl border border-slate-200 bg-white overflow-hidden">
        {blogs.length === 0 && <div className="p-10 text-center text-slate-500">No blog posts yet. Create your first one.</div>}
        {blogs.map((b) => (
          <div key={b.id} data-testid={`blog-row-${b.id}`} className="flex flex-col sm:flex-row sm:items-center gap-3 px-5 py-4 border-b border-slate-100 last:border-0">
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2 flex-wrap">
                <span className="text-xs uppercase tracking-widest text-blue-700">{b.category}</span>
                <span className={`text-[10px] px-2 py-0.5 rounded-full inline-flex items-center gap-1 ${b.is_published ? "bg-emerald-50 text-emerald-700" : "bg-slate-100 text-slate-500"}`}>
                  {b.is_published ? <><Eye size={10} /> Published</> : <><EyeOff size={10} /> Draft</>}
                </span>
              </div>
              <div className="mt-1 font-medium text-slate-900 truncate">{b.title}</div>
              <div className="text-xs text-slate-500 mt-0.5">/blog/{b.slug}</div>
            </div>
            <div className="flex gap-2">
              <button data-testid={`blog-edit-${b.id}`} onClick={() => start(b)} className="text-sm px-3 py-1.5 rounded-lg border border-slate-200 text-slate-700 hover:bg-slate-50">Edit</button>
              <button data-testid={`blog-delete-${b.id}`} onClick={() => remove(b)} className="text-sm px-3 py-1.5 rounded-lg border border-red-200 text-red-700 hover:bg-red-50 inline-flex items-center gap-1"><Trash2 size={12} /> Delete</button>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
